import React from 'react';
import { Dropdown, Empty } from 'antd';
import type { MenuProps } from 'antd';
import {
  Channel,
  ChannelConfig,
  NotificationConfig,
  StateConfig,
  getAddableStates,
  compareStatesByFlow,
} from 'services/NotificationTemplatesService';
import useStyles from './styles';
import StateCard from './StateCard';

interface ChannelSectionProps {
  classes: ReturnType<typeof useStyles>;
  channel: Channel;
  channelConfig: ChannelConfig;
  fullConfig: NotificationConfig;
  onToggleState: (stateCode: number, enabled: boolean) => void;
  onAddState: (stateCode: number) => void;
  onRemoveState: (stateCode: number) => void;
  onPreview: (state: StateConfig) => void;
  onEditOrMap: (state: StateConfig) => void;
}

const ChannelSection = ({
  classes,
  channel,
  channelConfig,
  fullConfig,
  onToggleState,
  onAddState,
  onRemoveState,
  onPreview,
  onEditOrMap,
}: ChannelSectionProps) => {
  const isWsp = channel === 'whatsapp';
  const activeCount = channelConfig.states.filter((s) => s.enabled).length;

  const orderedStates = [...channelConfig.states].sort((a, b) =>
    compareStatesByFlow(a.stateCode, b.stateCode),
  );
  const addable = getAddableStates(fullConfig, channel).sort((a, b) =>
    compareStatesByFlow(a.stateCode, b.stateCode),
  );
  const addMenu: MenuProps = {
    items: addable.map((s) => ({
      key: String(s.stateCode),
      label: `${s.stateCode} · ${s.stateName}`,
    })),
    onClick: ({ key }) => onAddState(Number(key)),
  };

  return (
    <div className={classes.channel}>
      <div className={classes.channelBar}>
        <span
          className={`${classes.channelIcon} ${
            isWsp ? classes.channelIconWsp : classes.channelIconMail
          }`}
        >
          {isWsp ? '✆' : '✉'}
        </span>
        <div>
          <div className={classes.channelTitle}>
            {isWsp ? 'WhatsApp' : 'Email'}
          </div>
          <div className={classes.channelCount}>
            {`${activeCount} estados con notificación activa`}
          </div>
        </div>
      </div>

      <div className={classes.channelInner}>
        <div className={classes.cap}>
          {isWsp
            ? 'Un estado · una plantilla aprobada'
            : 'Un estado · una plantilla'}
        </div>

        <div className={classes.carousel}>
          {orderedStates.map((state) => (
            <StateCard
              key={`${channel}-${state.stateCode}`}
              classes={classes}
              channel={channel}
              state={state}
              onToggle={(enabled) => onToggleState(state.stateCode, enabled)}
              onRemove={() => onRemoveState(state.stateCode)}
              onPreview={() => onPreview(state)}
              onEditOrMap={() => onEditOrMap(state)}
            />
          ))}

          {addable.length > 0 && (
            <Dropdown menu={addMenu} trigger={['click']}>
              <div className={classes.addcard} role="button" tabIndex={0}>
                <div>
                  <div style={{ fontSize: 22 }}>＋</div>
                  Agregar estado
                </div>
              </div>
            </Dropdown>
          )}

          {channelConfig.states.length === 0 && (
            <Empty description="Sin estados configurados" />
          )}
        </div>

        {isWsp && (
          <div className={classes.note}>
            ⚠️
            <div>
              Las notificaciones por WhatsApp no están disponibles por el
              momento.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelSection;
